'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { OffersOffer, PaginationType } from '@/services/dataTypes'
import { apiSpecificOffers, apiOfferDetails } from '@/apis/offers'
import { apiExpiredOffers } from '@/apis/user'
import OffersListView from './OffersListView'
import ProductCommentModal from './ProductCommentModal'
import { getBaseImageUrl } from '@/constants/hooks'
import { Loader2, Clock } from 'lucide-react'

interface Props {
    initialOffers: OffersOffer[]
    companyId: string
    merchantId: string
    awaited_p_id: string
    mer_slug_type: string
    mer_slug: string
    offerBanner: OffersOffer[]
    domain: string
    merchantName: string
    merchantLogo?: string | null
    pagination: PaginationType
    ads_campaign: boolean
}

type FilterTab = 'all' | 'codes' | 'deals' | 'shipping'

const LazyLoadingOffers = ({
    initialOffers,
    companyId,
    merchantId,
    awaited_p_id,
    mer_slug_type,
    mer_slug,
    domain,
    merchantName,
    merchantLogo,
    pagination,
    ads_campaign
}: Props) => {
    const searchParams = useSearchParams()
    const router = useRouter()
    const pathname = usePathname()

    const [offers, setOffers] = useState<OffersOffer[]>(initialOffers)
    const [expiredOffers, setExpiredOffers] = useState<OffersOffer[]>([])
    const perPage = pagination?.per_page || initialOffers.length || 10
    const lastPage = pagination?.last_page || 1
    const [visible, setVisible] = useState(initialOffers.length || perPage)
    const [allFetched, setAllFetched] = useState(lastPage <= 1)
    const [activeTab, setActiveTab] = useState<FilterTab>('all')

    // Active Modal Item state for ?show=true&p_id=... flow
    const [activeItem, setActiveItem] = useState<OffersOffer | null>(null)

    useEffect(() => {
        setOffers(initialOffers)
        setVisible(initialOffers.length || perPage)
        setAllFetched(lastPage <= 1)
        apiExpiredOffers(merchantId, companyId).then(res => setExpiredOffers(res.data?.offers || [])).catch(() => { })

        // Fetch the remaining pages in the background so tab counts cover every offer
        if (lastPage <= 1) return
        let cancelled = false
        const pages = Array.from({ length: Math.min(lastPage, 11) - 1 }, (_, i) => i + 2)
        Promise.all(
            pages.map(p => apiSpecificOffers(merchantId, companyId, p).then(res => res?.data?.offers || []).catch(() => [] as OffersOffer[]))
        ).then(results => {
            if (cancelled) return
            setOffers([...initialOffers, ...results.flat()])
            setAllFetched(true)
        })
        return () => { cancelled = true }
    }, [initialOffers, merchantId, companyId, perPage, lastPage])

    // Detect ?show=true&p_id=... or awaited_p_id parameter from URL to open modal on store visit
    useEffect(() => {
        const showParam = searchParams.get('show')
        const pIdParam = searchParams.get('p_id') || awaited_p_id

        if ((showParam === 'true' || awaited_p_id) && pIdParam) {
            const pool = [...offers, ...expiredOffers]
            const match = pool.find(
                (item) => String(item?.offer?.unique_id) === pIdParam || String(item?.offer?.id) === pIdParam
            )
            if (match) {
                setActiveItem(match)
            } else if (!activeItem) {
                apiOfferDetails(pIdParam, companyId).then(res => {
                    const foundData = res?.data ?? (res as any)?.offer ?? null
                    if (foundData) {
                        setActiveItem({
                            id: foundData.id,
                            offer: foundData,
                            merchant: foundData.merchant || { merchant_name: merchantName, merchant_logo: '' },
                            status: 1
                        } as any)
                    }
                }).catch(() => { })
            }
        }
    }, [searchParams, offers, expiredOffers, awaited_p_id, companyId, merchantName])

    const handleCloseModal = useCallback(() => {
        setActiveItem(null)
        const params = new URLSearchParams(searchParams.toString())
        params.delete('show')
        params.delete('p_id')
        const query = params.toString()
        router.replace(`${pathname}${query ? `?${query}` : ''}`, { scroll: false })
    }, [searchParams, pathname, router])

    const loadMore = () => setVisible(v => v + perPage)

    // Filter calculations
    const activeOffers = offers.filter(o => o.status === 1 && o.offer.status === 1)

    // Sort: Codes come first, then deals
    const sortedOffers = [...activeOffers].sort((a, b) => {
        const aHasCode = !!a?.offer?.coupon_code
        const bHasCode = !!b?.offer?.coupon_code
        if (aHasCode && !bHasCode) return -1
        if (!aHasCode && bHasCode) return 1
        return 0
    })

    const codesCount = activeOffers.filter(o => !!o.offer?.coupon_code).length
    const dealsCount = activeOffers.filter(o => !o.offer?.coupon_code).length
    const shippingCount = activeOffers.filter(o => {
        const text = `${o.offer?.offer_title || ''} ${o.offer?.offer_detail || ''}`.toLowerCase()
        return text.includes('free shipping') || text.includes('free delivery')
    }).length

    const filteredOffers = sortedOffers.filter(offer => {
        if (activeTab === 'codes') return !!offer.offer?.coupon_code
        if (activeTab === 'deals') return !offer.offer?.coupon_code
        if (activeTab === 'shipping') {
            const text = `${offer.offer?.offer_title || ''} ${offer.offer?.offer_detail || ''}`.toLowerCase()
            return text.includes('free shipping') || text.includes('free delivery')
        }
        return true
    })
    const displayedOffers = filteredOffers.slice(0, visible)
    const remainingOffersCount = filteredOffers.length - displayedOffers.length
    const stillLoading = !allFetched
    const allCount = allFetched ? activeOffers.length : Math.max(pagination?.total || 0, activeOffers.length)
    const totalForTab = activeTab === 'all' ? allCount : filteredOffers.length

    return (
        <div className="space-y-5">
            {/* Filter Tabs */}
            <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setActiveTab('all')}
                        className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                            activeTab === 'all'
                                ? 'bg-[#071822] text-white shadow-xs'
                                : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
                        }`}
                    >
                        All ({allCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('codes')}
                        className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                            activeTab === 'codes'
                                ? 'bg-[#071822] text-white shadow-xs'
                                : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
                        }`}
                    >
                        Codes ({codesCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('deals')}
                        className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                            activeTab === 'deals'
                                ? 'bg-[#071822] text-white shadow-xs'
                                : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
                        }`}
                    >
                        Deals ({dealsCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('shipping')}
                        className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                            activeTab === 'shipping'
                                ? 'bg-[#071822] text-white shadow-xs'
                                : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
                        }`}
                    >
                        Free Shipping ({shippingCount})
                    </button>
                </div>

                {/* Subtext info */}
                <p className="text-xs text-slate-500 font-medium pt-1">
                    Showing {displayedOffers.length} of {totalForTab} offers. Newest verified codes come first, then deals.
                </p>
            </div>

            {/* Offers List */}
            <div className="space-y-4">
                {displayedOffers.length > 0 ? (
                    displayedOffers.map((offer) => (
                        <OffersListView
                            key={offer.id}
                            product={offer}
                            companyId={companyId}
                            mer_slug_type={mer_slug_type}
                            mer_slug={mer_slug}
                            domain={domain}
                            merchantLogo={merchantLogo}
                            ads_campaign={ads_campaign}
                            awaited_p_id={offer.offer.unique_id === awaited_p_id ? awaited_p_id : undefined}
                        />
                    ))
                ) : (
                    <div className="py-12 text-center bg-white rounded-2xl border border-slate-200/80 p-6">
                        <p className="text-sm font-bold text-slate-700">No {activeTab} available at this time</p>
                        <button
                            onClick={() => setActiveTab('all')}
                            className="mt-3 text-xs font-semibold text-[#00a87a] hover:underline cursor-pointer"
                        >
                            View all offers
                        </button>
                    </div>
                )}
            </div>

            {/* Show More Offers Button */}
            {(remainingOffersCount > 0 || (stillLoading && displayedOffers.length >= filteredOffers.length)) && (
                <div className="pt-2">
                    <button
                        onClick={loadMore}
                        disabled={remainingOffersCount <= 0}
                        className="w-full py-3.5 rounded-2xl border-2 border-emerald-300/80 hover:border-[#00a87a] text-[#056042] hover:text-[#00a87a] font-bold text-sm bg-white hover:bg-emerald-50/40 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs disabled:opacity-50"
                    >
                        {remainingOffersCount <= 0 ? (
                            <>
                                <Loader2 size={16} className="animate-spin text-[#00a87a]" />
                                <span>Loading more offers...</span>
                            </>
                        ) : (
                            <span>Show {Math.min(perPage, remainingOffersCount)} more {merchantName} offers ({remainingOffersCount} remaining)</span>
                        )}
                    </button>
                </div>
            )}

            {/* Expired Offers */}
            {expiredOffers?.length > 0 && (
                <div className="mt-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Clock size={15} className="text-gray-400" />
                        <h2 className="text-sm font-bold text-gray-500">
                            Old deals that might still work for {merchantName}
                        </h2>
                    </div>
                    <div className="space-y-4 opacity-70">
                        {expiredOffers.slice(0, 8).map((item, i) => (
                            <OffersListView
                                key={i}
                                product={item}
                                companyId={companyId}
                                mer_slug_type={mer_slug_type}
                                mer_slug={mer_slug}
                                domain={domain}
                                merchantLogo={merchantLogo}
                                ads_campaign={ads_campaign}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Modal popup when user clicks Get Deal to visit store */}
            {activeItem && (
                <ProductCommentModal
                    product={activeItem.offer as any}
                    merchantName={activeItem?.merchant?.merchant_name || merchantName}
                    merchantLogo={getBaseImageUrl(domain, activeItem?.merchant?.merchant_logo || '', '')}
                    domain={domain}
                    isOpen={!!activeItem}
                    onClose={handleCloseModal}
                    productImg={activeItem.offer?.product_image || (activeItem as any)?.product_image || (activeItem.offer as any)?.offer?.product_image || activeItem.offer?.banner_image || (activeItem as any)?.image}
                    companyId={companyId}
                />
            )}
        </div>
    )
}

export default LazyLoadingOffers
